// Must stay first: rewrites legacy #/home/<jobId> links before the router reads the URL.
import '@/routes/legacyHashLinks';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isExtensionHost } from '@/utils/extensionHost';
import { TenantProvider } from '@/tenant/TenantProvider';
import { I18nProvider } from '@/i18n/I18nProvider';
import { ThemeProviderWithApi } from '@/theme/ThemeProviderWithApi';
import { AuthProvider } from '@/auth/AuthProvider';
import { SessionExpiredModal } from '@/auth/SessionExpiredModal';
import { SignalRProvider } from '@/realtime/SignalRProvider';
import { queryClient } from '@/api/queryClient';
import { AppRouter } from '@/routes';
import { PwaUpdatePrompt } from '@/components/pwa/PwaUpdatePrompt';
import '@/theme/global.css';

/**
 * Provider hierarchy (order matters):
 * 1. QueryClientProvider - TanStack Query for data fetching
 * 2. TenantProvider - Resolves tenant from subdomain
 * 3. I18nProvider - Loads translations (needs Query)
 * 4. AuthProvider - Authentication (needs tenant for proper redirect)
 * 5. ThemeProviderWithApi - Applies tenant theme from ABP Settings
 * 6. SignalRProvider - Real-time communication
 *
 * Note: ThemeProviderWithApi is inside AuthProvider because theme settings
 * require authentication to access the ABP Settings API.
 */
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <I18nProvider>
          <AuthProvider>
            <ThemeProviderWithApi>
              <SignalRProvider>
                <AppRouter />
              </SignalRProvider>
              {/* Global session-expired modal — single mount, theme + i18n in
                  scope, driven by the auth session-expiry controller. */}
              <SessionExpiredModal />
              <PwaUpdatePrompt />
            </ThemeProviderWithApi>
          </AuthProvider>
        </I18nProvider>
      </TenantProvider>
      {/* Its floating button would cover the Chrome extension panel's footer (Sign out). */}
      {!isExtensionHost() && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>
);
